<template>
  <div class="tcv-wrap">

    <!-- Header: today label + month navigation -->
    <div class="tcv-header">
      <div class="tcv-today-label">
        <fa-icon :icon="['fas', 'calendar-alt']" class="tcv-today-icon" />
        Today: {{ todayLabel }}
      </div>
      <div class="tcv-nav">
        <button class="tcv-nav-btn" :disabled="calendarMonth === 1" title="Previous month" @click="$emit('prev-month')">
          <fa-icon :icon="['fas', 'chevron-left']" />
        </button>
        <span class="tcv-nav-month">{{ currentMonthLabel }}</span>
        <button class="tcv-nav-btn" :disabled="calendarMonth === 12" title="Next month" @click="$emit('next-month')">
          <fa-icon :icon="['fas', 'chevron-right']" />
        </button>
      </div>
    </div>

    <!-- Month bar: Jan–Dec quick-jump -->
    <div class="tcv-month-bar" ref="monthBar">
      <span class="tcv-month-slider" :style="sliderStyle"></span>
      <button
        v-for="(name, i) in monthNames"
        :key="i"
        class="tcv-month-btn"
        :class="{ 'is-active': calendarMonth === i + 1 }"
        @click="$emit('set-month', i + 1)"
      >{{ name }}</button>
    </div>

    <!-- Calendar grid -->
    <div class="tcv-grid-wrap">
      <!-- Weekday header: M T W T F S S -->
      <div class="tcv-weekdays">
        <div v-for="(d, i) in weekDayLabels" :key="i" class="tcv-weekday">{{ d }}</div>
      </div>

      <!-- Day cells -->
      <div class="tcv-days">
        <!-- Leading blanks before the 1st -->
        <div v-for="n in leadingBlanks" :key="'blank-' + n" class="tcv-day tcv-day--blank" />

        <!-- Actual days -->
        <div
          v-for="day in daysInMonth"
          :key="'day-' + day"
          class="tcv-day"
          :class="{
            'tcv-day--today': isTodayDay(day),
            'tcv-day--has-items': dayItems(day).length > 0,
            'tcv-day--weekend': isWeekend(day)
          }"
        >
          <span class="tcv-day-num" :class="{ 'tcv-day-num--today': isTodayDay(day) }">{{ day }}</span>

          <div class="tcv-day-groups">
            <!-- Admin dates for this day -->
            <div v-if="dayAdminDates(day).length" class="tcv-admin-dates">
              <template v-for="ad in dayAdminDates(day)">
                <app-tooltip
                  v-if="ad.description"
                  :key="'ad-' + ad.id"
                  :text="ad.description"
                  min-width="200px"
                  position="top"
                  class="tcv-admin-date-row"
                >
                  <div class="tcv-ad-inner">
                    <div class="tcv-ad-content">
                      <span class="tcv-ad-title" :class="{ 'tcv-ad-title--done': ad.status === 'done' }">{{ ad.title }}</span>
                      <span v-if="ad.time_val" class="tcv-ad-meta" :class="{ 'tcv-ad-meta--done': ad.status === 'done' }"><fa-icon :icon="['fas', 'clock']" class="tcv-ad-meta-icon" />{{ ad.time_val }}</span>
                      <span v-if="ad.location" class="tcv-ad-meta" :class="{ 'tcv-ad-meta--done': ad.status === 'done' }"><fa-icon :icon="['fas', 'map-marker-alt']" class="tcv-ad-meta-icon" />{{ ad.location }}</span>
                      <span v-if="ad.phone" class="tcv-ad-meta" :class="{ 'tcv-ad-meta--done': ad.status === 'done' }"><fa-icon :icon="['fas', 'phone-alt']" class="tcv-ad-meta-icon" />{{ ad.phone }}</span>
                      <span v-if="ad.email" class="tcv-ad-meta" :class="{ 'tcv-ad-meta--done': ad.status === 'done' }"><fa-icon :icon="['fas', 'envelope']" class="tcv-ad-meta-icon" />{{ ad.email }}</span>
                    </div>
                    <button
                      class="tcv-ad-checkbox"
                      :class="{ 'is-done': ad.status === 'done' }"
                      :title="ad.status === 'done' ? 'Mark as pending' : 'Mark as done'"
                      @click.stop="toggleAdminDate(ad)"
                    >
                      <fa-icon v-if="updatingAdminDate[ad.id]" :icon="['fas', 'spinner']" spin class="tcv-ad-spin" />
                      <span v-else class="tcv-ad-box"></span>
                    </button>
                  </div>
                </app-tooltip>
                <div v-else :key="'ad-plain-' + ad.id" class="tcv-admin-date-row">
                  <div class="tcv-ad-inner">
                    <div class="tcv-ad-content">
                      <span class="tcv-ad-title" :class="{ 'tcv-ad-title--done': ad.status === 'done' }">{{ ad.title }}</span>
                      <span v-if="ad.time_val" class="tcv-ad-meta"><fa-icon :icon="['fas', 'clock']" class="tcv-ad-meta-icon" />{{ ad.time_val }}</span>
                      <span v-if="ad.location" class="tcv-ad-meta"><fa-icon :icon="['fas', 'map-marker-alt']" class="tcv-ad-meta-icon" />{{ ad.location }}</span>
                      <span v-if="ad.phone" class="tcv-ad-meta"><fa-icon :icon="['fas', 'phone-alt']" class="tcv-ad-meta-icon" />{{ ad.phone }}</span>
                      <span v-if="ad.email" class="tcv-ad-meta"><fa-icon :icon="['fas', 'envelope']" class="tcv-ad-meta-icon" />{{ ad.email }}</span>
                    </div>
                    <button
                      class="tcv-ad-checkbox"
                      :class="{ 'is-done': ad.status === 'done' }"
                      :title="ad.status === 'done' ? 'Mark as pending' : 'Mark as done'"
                      @click.stop="toggleAdminDate(ad)"
                    >
                      <fa-icon v-if="updatingAdminDate[ad.id]" :icon="['fas', 'spinner']" spin class="tcv-ad-spin" />
                      <span v-else class="tcv-ad-box"></span>
                    </button>
                  </div>
                </div>
              </template>
            </div>

            <div
              v-for="group in groupedDayItems(day)"
              :key="group.key"
              class="tcv-group"
            >
              <!-- Group header: colored by deadline type -->
              <div class="tcv-group-header" :class="groupHeaderClass(group)">
                <span class="tcv-group-label">{{ group.displayName }}</span>
                <span class="tcv-group-badge">{{ group.items.length }}</span>
              </div>
              <!-- Items sorted A-Z by client name -->
              <div class="tcv-group-body">
                <app-tooltip
                  v-for="item in group.items"
                  :key="item._key"
                  :text="item.client.client"
                  position="top"
                  class="tcv-gi w-full"
                >
                  <div class="tcv-gi-init">{{ clientInitialDisplay(item.client) }}</div>
                  <div
                    class="tcv-gi-name"
                    :class="{
                      'tcv-gi-name--done': item.dl.status === 'completed',
                      'tcv-gi-name--overdue': isOverdue(item.dl)
                    }"
                  >{{ item.dl.status === 'completed' ? 'Completed' : 'Pending' }}</div>
                  <span
                    v-if="showStatusFor(item.dl)"
                    class="tcv-gi-dot"
                    :class="{
                      'tcv-gi-dot--done': item.dl.status === 'completed',
                      'tcv-gi-dot--overdue': isOverdue(item.dl)
                    }"
                  ></span>
                  <fa-icon
                    v-if="item.dl.is_adhoc ? updatingAdhoc[item.dl.id] : updatingTask[item._key]"
                    :icon="['fas', 'spinner']"
                    spin
                    class="tcv-gi-spinner"
                  />
                  <div
                    v-else
                    class="tcv-item-trigger"
                    @click.stop="openTcvDropdown(item, $event)"
                  ></div>
                </app-tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!--
      Portalled status dropdown — rendered here (Vue reactivity), moved to <body>
      in mounted() so position:fixed is always viewport-relative. One shared
      instance repositioned on each chip click. Uses ap-si-menu/ap-si-opt from
      _select-input.scss; tcv-dl-dropdown-menu is a scoped width override only.
    -->
    <div
      ref="tcvDlMenu"
      class="ap-si-menu tcv-dl-dropdown-menu"
      v-show="!!openDropdownKey"
      :style="dropdownStyle"
      @click.stop
    >
      <div
        class="ap-si-opt"
        :class="{ 'is-active': openDropdownDl && openDropdownDl.status !== 'completed' }"
        @click="selectTcvStatus('pending')"
      >
        <span class="ap-si-opt-label">Pending</span>
        <fa-icon
          v-if="openDropdownDl && openDropdownDl.status !== 'completed'"
          :icon="['fas', 'check']"
          class="ap-si-opt-check"
        />
      </div>
      <div
        class="ap-si-opt"
        :class="{ 'is-active': openDropdownDl && openDropdownDl.status === 'completed' }"
        @click="selectTcvStatus('completed')"
      >
        <span class="ap-si-opt-label">Completed</span>
        <fa-icon
          v-if="openDropdownDl && openDropdownDl.status === 'completed'"
          :icon="['fas', 'check']"
          class="ap-si-opt-check"
        />
      </div>
    </div>

  </div>
</template>

<script>
import AppTooltip from './AppTooltip.vue'
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export default {
  name: 'task-calendar-view',
  components: { AppTooltip },
  props: {
    clients: { type: Array, default: () => [] },
    calendarMonth: { type: Number, required: true },
    calendarYear: { type: Number, required: true },
    currentMonth: { type: Number, required: true },
    currentYear: { type: Number, required: true },
    selectedSlot: { type: String, default: 'current' },
    monthNames: { type: Array, default: () => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] },
    updatingTask: { type: Object, default: () => ({}) },
    updatingAdhoc: { type: Object, default: () => ({}) },
    adminDates: { type: Array, default: () => [] },
    updatingAdminDate: { type: Object, default: () => ({}) },
    getDeadlines: { type: Function, required: true },
    deadlineClass: { type: Function, required: true }
  },
  data () {
    return {
      weekDayLabels: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
      sliderStyle: {},
      // Status dropdown (portal)
      openDropdownKey: null,
      openDropdownItem: null,
      openDropdownDl: null,
      dropdownStyle: {}
    }
  },
  mounted () {
    this.$nextTick(this.updateSlider)
    // Portal: move status dropdown to <body> so position:fixed is viewport-relative
    document.body.appendChild(this.$refs.tcvDlMenu)
    document.addEventListener('click', this.onTcvOutsideClick)
    window.addEventListener('scroll', this.onTcvScroll, true)
    window.addEventListener('resize', this.closeTcvDropdown)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onTcvOutsideClick)
    window.removeEventListener('scroll', this.onTcvScroll, true)
    window.removeEventListener('resize', this.closeTcvDropdown)
    const menu = this.$refs.tcvDlMenu
    if (menu && menu.parentNode === document.body) {
      document.body.removeChild(menu)
    }
  },
  watch: {
    calendarMonth () { this.$nextTick(this.updateSlider) }
  },
  computed: {
    todayLabel () {
      const now = new Date()
      return `${WEEKDAYS[now.getDay()]} ${now.getDate()} ${FULL_MONTHS[now.getMonth()]} ${now.getFullYear()}`
    },
    currentMonthLabel () {
      return `${FULL_MONTHS[this.calendarMonth - 1]} ${this.calendarYear}`
    },
    daysInMonth () {
      return new Date(this.calendarYear, this.calendarMonth, 0).getDate()
    },
    leadingBlanks () {
      // JS getDay(): 0=Sun … 6=Sat. Convert to Mon-first: Mon=0 … Sun=6
      const firstDay = new Date(this.calendarYear, this.calendarMonth - 1, 1).getDay()
      return (firstDay + 6) % 7
    }
  },
  methods: {
    updateSlider () {
      const bar = this.$refs.monthBar
      const btn = bar && bar.querySelector('.tcv-month-btn.is-active')
      if (!btn) return
      this.sliderStyle = { left: btn.offsetLeft + 'px', width: btn.offsetWidth + 'px' }
    },
    isTodayDay (day) {
      const now = new Date()
      return (
        this.selectedSlot === 'current' &&
        this.calendarMonth === now.getMonth() + 1 &&
        this.calendarYear === now.getFullYear() &&
        day === now.getDate()
      )
    },
    clientInitialDisplay (client) {
      return (client.initial || client.client || '?').slice(0, 4)
    },
    dayItems (day) {
      const lastDay = this.daysInMonth
      const items = []
      for (const client of this.clients) {
        const dls = this.getDeadlines(client, this.calendarMonth)
        for (const dl of dls) {
          if (!dl.date || dl.date.day == null) continue
          // Clamp month-end deadlines (e.g. day:31 in a 30-day month) to actual last day
          const targetDay = dl.date.day > lastDay ? lastDay : dl.date.day
          if (targetDay !== day) continue
          const key = dl.is_adhoc
            ? `adhoc-${dl.id}`
            : `${client.id}-${dl.name}${dl._month != null ? '-' + dl._month : ''}`
          items.push({ client, dl, _key: key })
        }
      }
      return items
    },
    showStatusFor (dl) {
      if (this.selectedSlot === 'prev') return true
      if (this.selectedSlot === 'current' && this.calendarMonth <= this.currentMonth) return true
      return dl.status === 'completed'
    },
    isOverdue (dl) {
      if (dl.status !== 'pending') return false
      if (this.selectedSlot === 'prev') return true
      if (this.selectedSlot === 'current') return this.calendarMonth < this.currentMonth
      return false
    },
    isWeekend (day) {
      // Mon-first grid: position 0=Mon … 5=Sat, 6=Sun
      const pos = (this.leadingBlanks + day - 1) % 7
      return pos === 5 || pos === 6
    },
    groupedDayItems (day) {
      const LABEL = {
        tax_filing: 'Tax Filing',
        cp: 'CP204',
        cp204: 'CP204',
        mitrs: 'MITRS',
        tp_reminder: 'TP Reminder',
        adhoc: 'Ad-hoc'
      }

      const displayName = key => {
        if (LABEL[key]) return LABEL[key]
        // cp204a6 → "CP204A(6)",  cp204a11 → "CP204A(11)"
        const m = key.match(/^cp204a(\d+)$/)
        if (m) return `CP204A(${m[1]})`
        return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      }

      // Returns [tier, sub] for deterministic ordering
      const sortKey = key => {
        if (key === 'tax_filing') return [0, 0]
        if (key === 'cp' || key === 'cp204') return [1, 0]
        const m = key.match(/^cp204a(\d+)$/)
        if (m) return [2, parseInt(m[1])]
        if (key === 'mitrs') return [3, 0]
        if (key === 'tp_reminder') return [4, 0]
        if (key === 'adhoc') return [6, 0]
        return [5, 0]
      }

      const items = this.dayItems(day)
      const groups = {}

      for (const item of items) {
        const key = item.dl.is_adhoc
          ? 'adhoc'
          : (item.dl.name || item.dl.display_name || 'other').toLowerCase()

        if (!groups[key]) {
          groups[key] = { key, displayName: displayName(key), items: [] }
        }
        groups[key].items.push(item)
      }

      for (const g of Object.values(groups)) {
        g.items.sort((a, b) =>
          (a.client.client || '').localeCompare(b.client.client || '')
        )
      }

      return Object.values(groups).sort((a, b) => {
        const [ta, sa] = sortKey(a.key)
        const [tb, sb] = sortKey(b.key)
        return ta !== tb ? ta - tb : sa - sb
      })
    },
    groupHeaderClass (group) {
      // Use first pending item's class for the group header color (ignores grey/completed override)
      const pending = group.items.find(i => i.dl.status !== 'completed')
      const ref = pending || group.items[0]
      return ref ? this.deadlineClass(ref.dl, this.calendarMonth) : ''
    },
    // ── Status dropdown (portal) ─────────────────────────────────────────────
    openTcvDropdown (item, e) {
      // Toggle closed if the same chip is clicked again
      if (this.openDropdownKey === item._key) {
        this.closeTcvDropdown()
        return
      }
      this.openDropdownKey = item._key
      this.openDropdownItem = item
      this.openDropdownDl = item.dl
      this._tcvTriggerEl = e.currentTarget
      this.$nextTick(this.positionTcvDropdown)
    },
    positionTcvDropdown () {
      if (!this._tcvTriggerEl) return
      const r = this._tcvTriggerEl.getBoundingClientRect()
      this.dropdownStyle = {
        position: 'fixed',
        top: (r.bottom + 4) + 'px',
        left: r.left + 'px',
        zIndex: 10000
      }
    },
    closeTcvDropdown () {
      this.openDropdownKey = null
      this.openDropdownItem = null
      this.openDropdownDl = null
      this._tcvTriggerEl = null
    },
    onTcvOutsideClick (e) {
      const menu = this.$refs.tcvDlMenu
      if (menu && menu.contains(e.target)) return
      if (this.openDropdownKey) this.closeTcvDropdown()
    },
    onTcvScroll () {
      if (this.openDropdownKey) this.positionTcvDropdown()
    },
    selectTcvStatus (status) {
      if (!this.openDropdownItem) return
      const { client, dl } = this.openDropdownItem
      this.closeTcvDropdown()
      this.$emit('update-status', client, dl, status)
    },
    // ── Admin dates ──────────────────────────────────────────────────────────
    dayAdminDates (day) {
      return this.adminDates.filter(ad =>
        ad.date && ad.date.month === this.calendarMonth && ad.date.day === day
      )
    },
    toggleAdminDate (ad) {
      const newStatus = ad.status === 'done' ? 'pending' : 'done'
      this.$emit('toggle-admin-date', ad, newStatus)
    }
    // ────────────────────────────────────────────────────────────────────────
  }
}
</script>

<style scoped>
/* ── Admin dates in calendar cells ──────────────────────────────────────── */
.tcv-admin-dates {
  margin-top: 4px;
  border-bottom: 1px dashed #bfdbfe;
  padding-top: 3px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 8px;
}

.tcv-admin-date-row {
  width: 100%;
  cursor: default;
}

.tcv-ad-inner {
  display: flex;
  align-items: flex-start;
  justify-content: end;
  gap: 4px;
  width: 100%;
}

.tcv-ad-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
  max-width: 70%;
  text-align: right;
}

.tcv-ad-title {
  font-size: 0.7rem;
  color: #1e40af;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

.tcv-ad-title--done {
  text-decoration: line-through;
  color: #9ca3af;
}

.tcv-ad-meta {
  font-size: 0.62rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
  justify-content: end;
}

.tcv-ad-meta--done {
  display: none;
}

.tcv-ad-meta-icon {
  font-size: 0.55rem;
  flex-shrink: 0;
  opacity: 0.7;
}

.tcv-ad-checkbox {
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: color 0.15s;
}
.tcv-ad-checkbox:hover { color: #1d4ed8; }
.tcv-ad-checkbox.is-done { color: #1d4ed8; }

.tcv-ad-box {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 1.5px solid currentColor;
  border-radius: 2px;
  position: relative;
  transition: background 0.15s, border-color 0.15s;
}
.tcv-ad-checkbox.is-done .tcv-ad-box {
  background: #1d4ed8;
  border-color: #1d4ed8;
}
.tcv-ad-checkbox.is-done .tcv-ad-box::after {
  content: '';
  position: absolute;
  left: 2px;
  top: 0px;
  width: 4px;
  height: 7px;
  border: 1.5px solid #fff;
  border-top: none;
  border-left: none;
  transform: rotate(45deg);
}

.tcv-ad-spin {
  font-size: 0.65rem;
  color: #6b7280;
}
</style>
